/**
 * InboxPage - Social Inbox für Kommentare und Reviews
 * 
 * Split-View Layout:
 * - Links: Interaktions-Liste mit Filtern
 * - Rechts: Detail-Panel mit KI-Antwortvorschlägen
 */

import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from 'sonner';
import { 
  Inbox, RefreshCw, Loader2, Send, Sparkles, Filter,
  MessageCircle, CheckCircle, XCircle, Clock, Star,
  Facebook, Instagram, Linkedin, ChevronRight, Copy,
  AlertTriangle, Check, X
} from "lucide-react";
import useIsMaster from "../hooks/useIsMaster";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Platform Icon Component
const PlatformIcon = ({ platform, size = 18 }) => {
  switch (platform?.toLowerCase()) {
    case 'facebook': return <Facebook size={size} className="text-blue-600" />;
    case 'instagram': return <Instagram size={size} className="text-pink-500" />;
    case 'linkedin': return <Linkedin size={size} className="text-blue-700" />;
    case 'google_review': return <Star size={size} className="text-amber-500" />;
    default: return <MessageCircle size={size} className="text-gray-500" />;
  }
};

// Status Badge Component
const StatusBadge = ({ status }) => {
  const configs = {
    new: { bg: "bg-blue-100", text: "text-blue-700", icon: Clock, label: "Neu" },
    replied: { bg: "bg-green-100", text: "text-green-700", icon: CheckCircle, label: "Beantwortet" },
    ignored: { bg: "bg-gray-100", text: "text-gray-500", icon: XCircle, label: "Ignoriert" }
  };
  const config = configs[status] || configs.new;
  const Icon = config.icon;
  
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
      <Icon size={12} />
      {config.label}
    </span>
  );
};

// Sentiment Badge
const SentimentBadge = ({ sentiment }) => {
  const configs = {
    positive: { bg: "bg-green-50", text: "text-green-600", label: "Positiv" },
    neutral: { bg: "bg-gray-50", text: "text-gray-600", label: "Neutral" },
    negative: { bg: "bg-red-50", text: "text-red-600", label: "Kritik" }
  };
  const config = configs[sentiment] || configs.neutral;
  
  return (
    <span className={`text-xs px-2 py-0.5 rounded ${config.bg} ${config.text}`}>
      {config.label}
    </span>
  );
};

// Interaction List Item
const InteractionItem = ({ interaction, isSelected, onClick }) => {
  const timeAgo = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffHours = Math.floor((now - date) / (1000 * 60 * 60));
    if (diffHours < 1) return "Gerade eben";
    if (diffHours < 24) return `vor ${diffHours}h`;
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays === 1) return "Gestern";
    return `vor ${diffDays} Tagen`;
  };

  return (
    <div 
      onClick={onClick}
      className={`p-4 border-b border-gray-100 cursor-pointer transition-colors ${
        isSelected ? 'bg-blue-50 border-l-4 border-l-blue-500' : 'hover:bg-gray-50'
      }`}
      data-testid={`interaction-item-${interaction.id}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2">
          <PlatformIcon platform={interaction.platform} size={20} />
          <span className="font-medium text-gray-800 text-sm">{interaction.author_name}</span>
          {/* ANA-03: Erfundene Beispieldaten müssen als solche erkennbar sein. */}
          {interaction.is_demo && (
            <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-purple-100 text-purple-700">
              Demo
            </span>
          )}
        </div>
        <StatusBadge status={interaction.status} />
      </div>
      <p className="text-gray-600 text-sm mt-2 line-clamp-2">{interaction.message_text}</p>
      <div className="flex items-center justify-between mt-2">
        <span className="text-xs text-gray-400">{timeAgo(interaction.created_at)}</span>
        <SentimentBadge sentiment={interaction.sentiment} />
      </div>
    </div>
  );
};

// Detail Panel Component
const DetailPanel = ({ interaction, onReply, onStatusChange, onRefresh }) => {
  const [replyText, setReplyText] = useState("");
  const [loading, setLoading] = useState(false);
  const [suggestLoading, setSuggestLoading] = useState(false);

  // Reset reply when interaction changes
  useEffect(() => {
    setReplyText("");
  }, [interaction?.id]);

  const handleSuggestReply = async () => {
    setSuggestLoading(true);
    try {
      const res = await axios.post(`${API}/interactions/${interaction.id}/suggest-reply`);
      if (res.data.success) {
        setReplyText(res.data.suggestion);
        toast.success(res.data.fallback ? 'Vorlage geladen' : 'KI-Vorschlag generiert');
      }
    } catch (error) {
      toast.error('Fehler beim Generieren des Vorschlags');
    } finally {
      setSuggestLoading(false);
    }
  };

  const handleSendReply = async () => {
    if (!replyText.trim()) {
      toast.error('Bitte Antworttext eingeben');
      return;
    }
    
    setLoading(true);
    try {
      const res = await axios.post(`${API}/interactions/${interaction.id}/reply`, {
        reply_text: replyText
      });
      
      if (res.data.success) {
        if (res.data.manual_action_needed) {
          toast.info('Antwort gespeichert - bitte manuell auf der Plattform einfügen');
        } else {
          toast.success('Antwort gesendet!');
        }
        onRefresh();
      }
    } catch (error) {
      toast.error('Fehler beim Senden');
    } finally {
      setLoading(false);
    }
  };

  const handleIgnore = async () => {
    try {
      await axios.put(`${API}/interactions/${interaction.id}/status`, { status: 'ignored' });
      toast.success('Als ignoriert markiert');
      onRefresh();
    } catch (error) {
      toast.error('Fehler beim Ändern des Status');
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(replyText);
    toast.success('In Zwischenablage kopiert');
  };

  if (!interaction) {
    return (
      <div className="flex-1 flex items-center justify-center bg-gray-50 text-gray-400">
        <div className="text-center">
          <Inbox size={48} className="mx-auto mb-4 opacity-50" />
          <p>Wähle eine Interaktion aus der Liste</p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 flex flex-col bg-white" data-testid="detail-panel">
      {/* Header */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <PlatformIcon platform={interaction.platform} size={24} />
            <div>
              <h3 className="font-semibold text-gray-900 flex items-center gap-2">
                {interaction.author_name}
                {interaction.is_demo && (
                  <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-purple-100 text-purple-700">
                    Demo — erfundenes Beispiel
                  </span>
                )}
              </h3>
              <p className="text-sm text-gray-500 capitalize">{interaction.platform.replace('_', ' ')}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <StatusBadge status={interaction.status} />
            <SentimentBadge sentiment={interaction.sentiment} />
          </div>
        </div>
      </div>

      {/* Message Content */}
      <div className="flex-1 overflow-auto p-4">
        <div className="bg-gray-50 rounded-lg p-4 mb-4">
          <p className="text-gray-700 whitespace-pre-wrap">{interaction.message_text}</p>
          <p className="text-xs text-gray-400 mt-3">
            {new Date(interaction.created_at).toLocaleString('de-DE')}
          </p>
        </div>

        {/* Previous Reply */}
        {interaction.reply_text && (
          <div className="bg-green-50 rounded-lg p-4 mb-4 border-l-4 border-green-500">
            <p className="text-xs text-green-600 font-medium mb-1">Deine Antwort:</p>
            <p className="text-gray-700">{interaction.reply_text}</p>
            <p className="text-xs text-gray-400 mt-2">
              {interaction.replied_at && new Date(interaction.replied_at).toLocaleString('de-DE')}
              {interaction.reply_sent_via_api === false && (
                <span className="ml-2 text-amber-600">(Manuell eingefügt)</span>
              )}
            </p>
          </div>
        )}

        {/* Reply Section - only show if not already replied */}
        {interaction.status !== 'replied' && (
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="font-medium text-gray-700">Antwort schreiben</h4>
              <button
                onClick={handleSuggestReply}
                disabled={suggestLoading}
                className="flex items-center gap-2 px-3 py-1.5 text-sm bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200 transition-colors"
                data-testid="suggest-reply-btn"
              >
                {suggestLoading ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <Sparkles size={14} />
                )}
                KI-Vorschlag
              </button>
            </div>
            
            <textarea
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              placeholder="Deine Antwort eingeben oder KI-Vorschlag holen..."
              className="w-full h-32 p-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
              data-testid="reply-textarea"
            />

            {/* Action Buttons */}
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                {replyText && (
                  <button
                    onClick={copyToClipboard}
                    className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100 rounded"
                    title="In Zwischenablage kopieren"
                  >
                    <Copy size={14} />
                    Kopieren
                  </button>
                )}
                <button
                  onClick={handleIgnore}
                  className="flex items-center gap-1 px-3 py-1.5 text-sm text-gray-500 hover:bg-gray-100 rounded"
                >
                  <X size={14} />
                  Ignorieren
                </button>
              </div>
              
              <button
                onClick={handleSendReply}
                disabled={loading || !replyText.trim()}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="send-reply-btn"
              >
                {loading ? (
                  <Loader2 size={16} className="animate-spin" />
                ) : (
                  <Send size={16} />
                )}
                Antwort speichern
              </button>
            </div>

            {/* Info about manual posting */}
            <div className="flex items-start gap-2 p-3 bg-amber-50 rounded-lg text-sm text-amber-700">
              <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
              <p>
                Die Antwort wird gespeichert. Du musst sie anschließend manuell auf der Plattform einfügen, 
                da automatisches Posten von Antworten aktuell nicht für alle Plattformen verfügbar ist.
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Main InboxPage Component
const InboxPage = () => {
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  // ANA-03: Der Demo-Daten-Knopf ist ein Betreiber-Werkzeug (Vorführung, App-Review).
  // useIsMaster ist fail-closed — solange die Rolle unbekannt ist, gilt „kein Master".
  const isMaster = useIsMaster();
  const [interactions, setInteractions] = useState([]);
  const [stats, setStats] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState("new");
  const [platformFilter, setPlatformFilter] = useState("all");

  // Load interactions
  const loadInteractions = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter && statusFilter !== "all") params.append("status", statusFilter);
      if (platformFilter && platformFilter !== "all") params.append("platform", platformFilter);
      
      const res = await axios.get(`${API}/interactions?${params.toString()}`);
      setInteractions(res.data.interactions || []);
      setStats(res.data.stats);
    } catch (error) {
      toast.error('Fehler beim Laden der Interaktionen');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, platformFilter]);

  useEffect(() => {
    loadInteractions();
  }, [loadInteractions]);

  // Sync interactions
  const handleSync = async () => {
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/interactions/sync`);
      toast.success(`Sync abgeschlossen: ${res.data.total_synced} neue Interaktionen`);
      loadInteractions();
    } catch (error) {
      toast.error('Fehler beim Synchronisieren');
    } finally {
      setSyncing(false);
    }
  };

  // Create demo data
  const handleCreateDemo = async () => {
    try {
      const res = await axios.post(`${API}/interactions/create-demo`);
      toast.success(res.data.message);
      loadInteractions();
    } catch (error) {
      toast.error('Fehler beim Erstellen der Demo-Daten');
    }
  };

  // Get selected interaction
  const selectedInteraction = interactions.find(i => i.id === selectedId);

  return (
    <div className="h-screen flex flex-col bg-gray-100" data-testid="inbox-page">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 p-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <Inbox className="text-blue-500" />
              Social Inbox
            </h1>
            {stats && (
              <p className="text-sm text-gray-500 mt-1">
                {stats.new} neu · {stats.replied} beantwortet · {stats.total} gesamt
              </p>
            )}
          </div>
          
          <div className="flex items-center gap-3">
            {/* Filters */}
            <div className="flex items-center gap-2 bg-gray-100 rounded-lg p-1">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="text-sm bg-transparent border-0 focus:ring-0 pr-8"
                data-testid="status-filter"
              >
                <option value="all">Alle Status</option>
                <option value="new">Neu</option>
                <option value="replied">Beantwortet</option>
                <option value="ignored">Ignoriert</option>
              </select>
              
              <select
                value={platformFilter}
                onChange={(e) => setPlatformFilter(e.target.value)}
                className="text-sm bg-transparent border-0 focus:ring-0 pr-8"
                data-testid="platform-filter"
              >
                <option value="all">Alle Plattformen</option>
                <option value="facebook">Facebook</option>
                <option value="instagram">Instagram</option>
                <option value="linkedin">LinkedIn</option>
                <option value="google_review">Google Reviews</option>
              </select>
            </div>

            <button
              onClick={handleSync}
              disabled={syncing}
              className="flex items-center gap-2 px-3 py-2 text-sm bg-white border border-gray-200 rounded-lg hover:bg-gray-50"
              data-testid="sync-btn"
            >
              <RefreshCw size={16} className={syncing ? 'animate-spin' : ''} />
              Sync
            </button>

            {/* ANA-03 (16.08.2026): Dieser Knopf stand JEDEM Kunden zur Verfügung —
                und weil eine frische Inbox immer leer ist, sah ihn jeder Neukunde
                sofort. Ein Klick schrieb sechs frei erfundene Personen samt einer
                erfundenen Fünf-Sterne-Google-Bewertung in dieselbe Sammlung wie echte
                Kommentare, ohne jede Kennzeichnung. Wer damit arbeitet, hält
                Erfundenes für Echtes. Jetzt nur noch für den Betreiber (Vorführung,
                Plattform-Review); die Datensätze tragen zusätzlich ein sichtbares
                DEMO-Kennzeichen. */}
            {isMaster && interactions.length === 0 && (
              <button
                onClick={handleCreateDemo}
                className="flex items-center gap-2 px-3 py-2 text-sm bg-purple-100 text-purple-700 rounded-lg hover:bg-purple-200"
                title="Nur für Vorführungen: legt sechs erfundene Beispiel-Interaktionen an, deutlich als DEMO gekennzeichnet."
              >
                <Sparkles size={16} />
                Demo-Daten
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Main Content - Split View */}
      <div className="flex-1 flex overflow-hidden max-w-7xl mx-auto w-full">
        {/* Left Panel - List */}
        <div className="w-96 bg-white border-r border-gray-200 flex flex-col">
          {loading ? (
            <div className="flex-1 flex items-center justify-center">
              <Loader2 size={24} className="animate-spin text-blue-500" />
            </div>
          ) : interactions.length === 0 ? (
            <div className="flex-1 flex items-center justify-center p-4">
              <div className="text-center text-gray-400">
                <Inbox size={40} className="mx-auto mb-3 opacity-50" />
                <p className="font-medium">Keine Interaktionen</p>
                <p className="text-sm mt-1">Klicke auf "Sync" oder "Demo-Daten"</p>
              </div>
            </div>
          ) : (
            <div className="flex-1 overflow-auto">
              {interactions.map(interaction => (
                <InteractionItem
                  key={interaction.id}
                  interaction={interaction}
                  isSelected={selectedId === interaction.id}
                  onClick={() => setSelectedId(interaction.id)}
                />
              ))}
            </div>
          )}
        </div>

        {/* Right Panel - Detail */}
        <DetailPanel
          interaction={selectedInteraction}
          onRefresh={() => {
            loadInteractions();
            setSelectedId(null);
          }}
        />
      </div>
    </div>
  );
};

export default InboxPage;
